import { Component, Input } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment-timezone';
import { SelectItem } from 'primeng/api';
import { isEqual } from 'lodash';

import { UnifiedStoryResponseModel } from '../../../../api/story/model/story-response.model';
import { StatusEnum } from '../../enums/status.enum';
import { NewsroomRightSideBarInterface } from './newsroom-right-side-bar.interface';
import { storyConstants } from '../story/story.component';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { TimeModel } from '../../../../Common/Forms/models/time.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';

const newsroomRightSideBarComponentText = {
    publishNow: 'Publish Now',
    publishLater: 'Publish Later'
};
@Component({
    selector: 'agpr-newsroom-right-side-bar',
    templateUrl: './newsroom-right-side-bar.component.html',
    styleUrls: ['./newsroom-right-side-bar.component.scss'],
    standalone: false
})
export class NewsroomRightSideBarComponent  {

    @Input() localization = newsroomRightSideBarComponentText;
    @Input() storyResponseModel: UnifiedStoryResponseModel = new UnifiedStoryResponseModel();
    @Input() public rightSideBarInterface: NewsroomRightSideBarInterface;
    @Input() public showLogoButton: boolean = false;
    @Input() public aboutUsButtonDisabled: boolean = false;
    @Input() public aboutUsButton: boolean = false;
    @Input() public linkButton: boolean = false;
    @Input() public statusId: number;
    @Input() public showPublishLater: boolean = false;
    @Input() public subjectDropDownDisabled: boolean = false;
    @Input() public reservedUrl: string = null;

    @Input() public id: number;
    @Input() public isLoader: boolean = false;
    @Input() public storyCreatedBy: string;
    @Input() public storyModifiedBy: string;
    @Input() public minDate = new Date();
    @Input() public preferences: PreferencesModel;
    @Input() remainingTextContents: number = 0;
    @Input() remainingImageContents: number = 0;

    @Input() public set subjectList(subjects: SelectItem[]) {
        this.subjectOptions = subjects || [];
        this.subjects$ = of(this.subjectOptions);

        this.selectedSubjects = this.subjectOptions.filter(subject => this.selectedSubjectIds.includes(subject.value));
    }
    @Input() public set selectedSubject(selectedSubjects: number[]) {
        this.selectedSubjectIds = selectedSubjects || [];

        this.selectedSubjects = this.subjectOptions.filter(subject => this.selectedSubjectIds.includes(subject.value));
    }

    selectedSubjectIds: number[] = [];
    selectedSubjects: SelectItem[] = [];
    subjectOptions: SelectItem[] = [];
    subjects$: Observable<SelectItem[]>;

    oldScheduledDate: Date;
    oldScheduledTime: TimeModel;

    subjectDefault: string = storyConstants.subjectDefault;

    readonly buttonTypes = ButtonTypesEnum;
    readonly statusEnum = StatusEnum;
    readonly autocompleteOptions: AutocompleteChipsOptions = {
        forceSelection: true,
        dropdown: true
    };

    constructor() {
        // have to set this in the constructor, because our lint rules don't like us defining functions before others
        this.autocompleteOptions.suggestionsFetcher = this.getSubjects;
    }

    readonly getSubjects = (query: string) => {
        if (query) {
            const queryLower = query.toLocaleLowerCase();
            return this.subjects$.pipe(map(
                subs => subs.filter(sub => sub.label.toLocaleLowerCase().includes(queryLower))
            ));
        }
        return this.subjects$;
    };

    public enableDeliveryScheduler(): boolean {
        const isRetracted: boolean =  this.statusId === this.statusEnum.RETRACTED;
        let enableDeliveryScheduler: boolean = false;

        if (!isRetracted) {
            enableDeliveryScheduler = true;
        }

        return enableDeliveryScheduler;
    }

    /**
     * Enable Reserved Url
     */
    public enableReservedUrl(): boolean {
        let reservedUrlEnabled = false;
        const isPublished = this.storyResponseModel.statusId === this.statusEnum.PUBLISHED;

        if ((!isPublished) && this.storyResponseModel.headline) {
            reservedUrlEnabled = true;
        }

        return reservedUrlEnabled;
    }
    public imageClick(type: string): void {
        this.rightSideBarInterface.imageClick(type);
    }
    public textClick(): void {
        this.rightSideBarInterface.textClick();
    }
    public toggleAboutUs(): void {
        this.rightSideBarInterface.toggleAboutUs();
    }
    public toggleLink(): void {
        this.rightSideBarInterface.toggleLink();
    }
    public setSubjects(): void {
        const tempSelectedSubjectIds = this.selectedSubjects.map(subject => subject.value);
        if (!isEqual(this.selectedSubjectIds?.slice().sort(), tempSelectedSubjectIds?.slice().sort())) {
            this.selectedSubjectIds = tempSelectedSubjectIds;
            this.rightSideBarInterface.setSubjects(this.selectedSubjectIds);
        }
    }

    public scheduledDateSelected(date: Date): void {
        if (!this.oldScheduledDate || date.getDate() !== this.oldScheduledDate.getDate()) {
            this.oldScheduledDate = date;
            this.rightSideBarInterface.isModelChanged = true;
        }
    }

    public schedulingTypeSelected(): void {
        this.rightSideBarInterface.publishDate = new Date();
        this.rightSideBarInterface.publishTime = new TimeModel(this.preferences.timeFormat);
        this.rightSideBarInterface.publishTime.timeZone =
            this.preferences && this.preferences.timeZone
                ? this.preferences.timeZone
                : 'UTC';
        this.rightSideBarInterface.isModelChanged = true;
    }

    public timeChanged(time: TimeModel): void {
        if (!this.oldScheduledTime || !time.equals(this.oldScheduledTime)) {
            this.oldScheduledTime = time;
            this.rightSideBarInterface.isModelChanged = true;
        }
    }

    /*
    * Get formatted date string with timezone
    */
    public getFormattedTime(date: number, timezone: string): string {
        let momentDate = moment(date);
        if (timezone) {
            momentDate = momentDate.tz(timezone);
        } else {
            momentDate = momentDate.tz('UTC');
        }
        return momentDate.format(this.preferences?.getDateFormatString(true, true, true));
    }

}
